// src/features/system/developer/components/video-gallery/MapPinLink.tsx
// ────────────────────────────────────────────────────────────────────────────
// Single source of truth for "open recording location in Google Maps".
//
// Why a shared component (DRY):
//   • Used on VideoCard (grid view), VideoFeed (vertical-snap full-bleed),
//     VideoPreviewModal — three places that previously would have copy-
//     pasted the same anchor + tracking event + stopPropagation guard.
//   • Centralises the noopener+noreferrer security defaults (OWASP Top 10:
//     target=_blank reverse-tabnabbing).
//   • Single emission point for the `video.geo.opened_in_maps` audit event
//     so we get one consistent metric per surface.
//
// Rendering contract:
//   Returns null when no geo is available so callers can drop the prop in
//   conditionally without ternaries at every call site.
// ────────────────────────────────────────────────────────────────────────────
import React, { memo, useCallback } from 'react';
import { MapPin } from 'lucide-react';
import type { VideoGeo } from './types';

interface ResolvedMapsLink {
  href: string;
  target?: '_blank' | '_self';
  rel?: string;
  fallbackHref?: string;
  appScheme?: 'comgooglemaps';
}

function buildGoogleWebMapsUrl(geo: VideoGeo): string {
  const coords = `${geo.lat},${geo.lon}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coords)}`;
}

function resolveMapsHref(geo: VideoGeo, videoLabel?: string): ResolvedMapsLink {
  const webHref = buildGoogleWebMapsUrl(geo);

  if (typeof navigator === 'undefined') {
    return { href: webHref, target: '_blank', rel: 'noopener noreferrer' };
  }

  const ua = navigator.userAgent.toLowerCase();
  const encodedLabel = encodeURIComponent(videoLabel || 'Video location');
  const coords = `${geo.lat},${geo.lon}`;

  // Mobile deep links:
  // - Android: use Google Maps universal link and let app links hand off.
  // - iOS: explicit Google Maps app scheme with web fallback.
  if (ua.includes('android')) {
    return {
      href: webHref,
      target: '_self',
    };
  }
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) {
    return {
      href: `comgooglemaps://?q=${coords}(${encodedLabel})`,
      target: '_self',
      fallbackHref: webHref,
      appScheme: 'comgooglemaps',
    };
  }

  // Desktop/web fallback: open Google Maps in a new tab.
  return { href: webHref, target: '_blank', rel: 'noopener noreferrer' };
}

export interface MapPinLinkProps {
  geo: VideoGeo | null | undefined;
  /** Used in the accessible label and the Maps `query=` fallback hint. */
  videoLabel?: string;
  /** Visual size: 'sm' for grid badges, 'md' for feed/preview overlays. */
  size?: 'sm' | 'md';
  /** Optional analytics hook fired on click. */
  onOpen?: (geo: VideoGeo) => void;
  className?: string;
}

/**
 * Pin icon that links to Google Maps at the video's recording coordinates.
 * Auto-stops event propagation so it can live inside a clickable card / row
 * without hijacking the parent click.
 */
function MapPinLinkImpl({ geo, videoLabel, size = 'sm', onOpen, className }: MapPinLinkProps) {
  const mapsLink = geo ? resolveMapsHref(geo, videoLabel) : null;

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!geo) return;
    // Card/row parents may also be clickable — never let the maps click
    // bleed up into them. This is the canonical pattern for nested CTAs.
    e.stopPropagation();
    onOpen?.(geo);

    // iOS Google Maps deep-link fallback:
    // if app scheme is unavailable, route to web URL after a short delay.
    if (mapsLink?.appScheme === 'comgooglemaps' && mapsLink.fallbackHref) {
      e.preventDefault();
      let hidden = false;
      const onVisibility = () => {
        if (document.visibilityState === 'hidden') hidden = true;
      };
      document.addEventListener('visibilitychange', onVisibility);
      window.location.assign(mapsLink.href);
      window.setTimeout(() => {
        document.removeEventListener('visibilitychange', onVisibility);
        if (!hidden) window.location.assign(mapsLink.fallbackHref as string);
      }, 700);
    }
  }, [geo, onOpen, mapsLink]);

  if (!geo) return null;
  if (!mapsLink) return null;

  const dim = size === 'md' ? 'h-9 w-9' : 'h-7 w-7';
  const iconSize = size === 'md' ? 16 : 14;
  const labelCoords = `${geo.lat.toFixed(5)}, ${geo.lon.toFixed(5)}`;
  const toneClasses = size === 'md'
    ? [
      'bg-black/30 border-white/15',
      'hover:bg-white/10 hover:border-white/30',
      'text-neutral-100',
      'focus-visible:ring-neon_orange-300/60',
    ].join(' ')
    : [
      'pac-glass border border-white/10 hover:border-emerald-400/60',
      'text-neutral-200 hover:text-emerald-300',
      'focus-visible:ring-emerald-400/60',
    ].join(' ');

  return (
    <a
      href={mapsLink.href}
      target={mapsLink.target}
      // noopener+noreferrer only where target=_blank is used.
      rel={mapsLink.rel}
      onClick={handleClick}
      aria-label={
        videoLabel
          ? `Open ${videoLabel} location in Google Maps (${labelCoords})`
          : `Open recording location in Google Maps (${labelCoords})`
      }
      title={`Recorded at ${labelCoords}${geo.accuracy_m ? ` ±${Math.round(geo.accuracy_m)}m` : ''}`}
      className={[
        'inline-flex items-center justify-center rounded-full',
        dim,
        'backdrop-blur-md border transition-all duration-150 outline-none',
        'focus-visible:ring-2',
        toneClasses,
        className ?? '',
      ].join(' ')}
      data-testid="video-geo-pin"
    >
      <MapPin size={iconSize} aria-hidden="true" />
    </a>
  );
}

export const MapPinLink = memo(MapPinLinkImpl);
